import { useState, useEffect } from "react";
import { Box, Button, CircularProgress, IconButton } from "@mui/material";

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SyncIcon from '@mui/icons-material/Sync';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CloseIcon from '@mui/icons-material/Close';

import AppCard, { CARD_HEADER_SX, SECTION_TITLE_SX } from "@plannel/components/ui/AppCard";
import StatPill from "@plannel/components/ui/StatPill";
import useJobProgress from "@plannel/hooks/useJobProgress";

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEqual } from "lodash";
import { format } from "date-fns";

import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import dataLoadValidationService from "@plannel/services/data/data-load-validation-service";
import appConfigService from "@plannel/services/system/app-config-service";
import "@plannel/scss/components/DataLoadValidation.scss";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import ColorUtil from "@plannel/pages/utils/color-util";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import DateUtil from "@plannel/utils/date-util";
import { FS } from "@plannel/utils/font-util";

const SYNC_CONFIG_CD = "DATA_VAL_SYNC";
const DIALOG_TITLE = {
    DATA_VALIDATION: "dataValidation",
    SYNC: "sync"
};
const YEAR = "year";
const MONTH = "month";
const DAY = "day";
const START = "start";
const END = "end";

const { snackBarMessageColor } = ColorUtil;

const DataValidation = ({ t, viewName }) => {
    const reduxDispatch = useDispatch();
    const { isPermitted } = useMenuAccess();

    const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
    const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
    const [validationInfos, setValidationInfos] = useState([]);
    const [validationRun, setValidationRun] = useState(false);
    /* 방금 끝난 검사 결과 — 배너로 보여준다(오류 건수는 갱신된 validationInfos 에서 읽는다) */
    const [validationResult, setValidationResult] = useState(null);
    /* 지금 돌고 있는 항목 — null 이면 전체. 부분 재검사 때 관계없는 카드까지 스피너가 돌지 않게 한다 */
    const [runningTitles, setRunningTitles] = useState(null);
    const [lastSyncDates, setLastSyncDates] = useState({});

    const now = new ZDate();
    const syncTargets = [
        "checkCycleCalendar", "checkLocation", "checkWorkcenter", "checkBod",
        "checkResource", "checkCustomerLocation", "checkCustomerItem",
        "checkLocationItem", "checkBom", "checkBodItem", "checkRoute",
        "checkSupplierItem", "checkResourceCapacity", "checkRouting", "checkWip", "checkPostnHierarchy"
    ];

    const onDialogClose = () => {
        setDialogInfo({ open: false, title: "", content: "" });
    };

    const handleDialog = (response, _, el) => {
        if (response && el) {
            el();
        }
    };

    const handleDialogOpen = (action, jobTarget, el) => {
        switch (action) {
            case DIALOG_TITLE.DATA_VALIDATION: {
                setDialogInfo({ open: true, title: `${t(action)} [${t(jobTarget)}]`, content: `${t("MSG_ValidConfirm")}`, el });
                break;
            }
            case DIALOG_TITLE.SYNC: {
                const filteredTitleList = jobTarget.titleList && jobTarget.titleList.length > 0
                    ? jobTarget.titleList.filter(item => item.syncable !== false && (item.dataCount || 0) > 0)
                    : [];

                const titleListMessage = filteredTitleList.length > 0
                    ? '\n' + filteredTitleList.map(item => `- ${t(item.title, item.params ? Object.fromEntries(
                        Object.entries(item.params).map(([key, value]) => [key, t(value)])
                    ) : {})} (${t("errorCnt")}: ${item.dataCount})`).join('\n')
                    : '';

                setDialogInfo({
                    open: true,
                    title: `${t(action)}`,
                    content: `${t("MSG_SyncConfirm")}${titleListMessage}`,
                    el,
                    maxWidth: "sm"
                });
                break;
            }
            default: 
                break;
        }
    };

    /* 새 결과가 화면에 들어온 시점을 호출부가 알 수 있도록 프로미스를 돌려준다 */
    const retrieveValidations = () =>
        dataLoadValidationService.getValidation()
            .then(response => {
                setValidationInfos(response.data);
            })
            .catch(error => { debug(error); });

    const retrieveLastSyncDates = () => {
        appConfigService.getAppConfigObjVal(SYNC_CONFIG_CD)
            .then(response => {
                setLastSyncDates(response.data ?? {});
            })
            .catch(error => { debug(error); });
    };

    const dateFormatter = (stringDate) => {
        return format(new ZDate(stringDate), "yyyy-MM-dd");
    };

    /* 머리글 배지용 요약 — 전체 오류 건수와 가장 최근 검증일 */
    const totalErrorCount = (validationInfos ?? []).reduce((sum, info) => {
        const rows = info?.titleList ?? [];
        const count = rows.length > 0
            ? rows.reduce((acc, row) => acc + (row.dataCount || 0), 0)
            : (info?.dataCount || 0);
        return sum + count;
    }, 0);

    /* 오류가 있는 항목만 — 상단 요약에서 "어디가 문제인지" 를 바로 짚어 준다 */
    const failingInfos = (validationInfos ?? [])
        .map(info => {
            const rows = info?.titleList ?? [];
            const count = rows.length > 0
                ? rows.reduce((acc, row) => acc + (row.dataCount || 0), 0)
                : (info?.dataCount || 0);
            return { title: info.title, count };
        })
        .filter(item => item.count > 0)
        .sort((a, b) => b.count - a.count);

    /* 항목 하나의 오류 건수 — titleList 가 있으면 그 합계가 그 항목의 건수다 */
    const errorCountOf = (info) => {
        const rows = info?.titleList ?? [];
        return rows.length > 0
            ? rows.reduce((acc, row) => acc + (row.dataCount || 0), 0)
            : (info?.dataCount || 0);
    };

    /*
     * 배너에 실을 건수 — titles 가 있으면 **그 항목들만** 센다.
     *   전체 합계를 쓰면 "WIP 동기화 완료" 옆에 다른 항목의 오류까지 얹혀서
     *   방금 고친 것이 반영이 안 된 것처럼 보인다.
     */
    const errorCountFor = (titles) => (validationInfos ?? [])
        .filter(info => titles === null || titles.includes(info.title))
        .reduce((sum, info) => sum + errorCountOf(info), 0);

    const cardDomId = (title) => `validation-card-${title}`;

    const scrollToCard = (title) => {
        document.getElementById(cardDomId(title))?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const mostRecentValidation = (validationInfos ?? []).reduce((latest, item) => {
        if (!item.lastValidationDate) return latest;
        if (!latest) return item.lastValidationDate;
        return new Date(item.lastValidationDate) > new Date(latest) ? item.lastValidationDate : latest;
    }, null);

    const lastValidationLabel = mostRecentValidation
        ? dateFormatter(mostRecentValidation)
        : t("validationRecommend");

    /*
     * 진행 여부의 진실은 서버에 있다 — 탭을 옮기면 이 화면이 통째로 언마운트돼
     * 로컬 state(validationRun) 가 사라지기 때문이다. 돌아왔을 때도 실제로 돌고 있으면
     * 그대로 진행 중으로 보이고, 끝나면 결과를 다시 읽는다.
     */
    const watchedTitles = (validationInfos ?? []).map(info => info.title).filter(Boolean);
    const { runningNames: serverRunningTitles, running: serverRunning, probe: probeJobs } =
        useJobProgress(watchedTitles, () => (
            /* 훅이 이 프로미스를 기다린 뒤에야 "끝남" 으로 바꾼다 — 옛 숫자가 노출되는 창을 없앤다 */
            retrieveValidations().then(() => {
                setValidationResult(prev => prev ?? { detail: null, titles: null });
            })
        ));

    /*
     * 검증 상태 아이콘 — File Upload & Validation 좌측 목록과 같은 어휘를 쓴다.
     *   오류 있음 = 채운 경고 / 검증됨 = 채운 체크 / 데이터 없음 = 빈 원 / 아직 안 함 = 물음표
     * 기본 크기(24px) 대신 18px 로 낮춘다 — 제목보다 무거우면 목록이 아이콘 열처럼 보인다.
     * 데이터 없음에 쓰던 대시(—)는 "해당 없음"으로 읽혀 "아직 안 함"과 구분되지 않아 빈 원으로 바꾼다.
     */
    /*
     * 이 항목이 지금 돌고 있나.
     *   로컬(이 탭에서 눌렀다) 또는 서버(다른 탭에서 돌고 있다) 어느 쪽이든 진행 중이다.
     *   아이콘·카드 흐림·동기화 버튼이 같은 판정을 써야 한 카드가 따로 놀지 않는다.
     */
    const isTitleRunning = (title) => {
        const localRunning = validationRun && (runningTitles === null || (title != null && runningTitles.includes(title)));
        return localRunning || (title != null && serverRunningTitles.includes(title));
    };

    const STATE_ICON_SX = { fontSize: 18, flexShrink: 0 };

    const ValidationStateIcon = (validationProps) => {
        const { dataCount, lastValidationDate, noDataFlg, title } = validationProps;

        if (isTitleRunning(title)) {
            return <CircularProgress size={16} />;
        }

        if (dataCount > 0) {
            return <ErrorRoundedIcon sx={{ ...STATE_ICON_SX, color: "error.main" }} />;
        }

        if (noDataFlg) {
            return <RadioButtonUncheckedIcon sx={{ ...STATE_ICON_SX, color: "grey.400" }} />;
        }

        if (lastValidationDate) {
            return <CheckCircleRoundedIcon sx={{ ...STATE_ICON_SX, color: "success.main" }} />;
        }

        return <HelpOutlineRoundedIcon sx={{ ...STATE_ICON_SX, color: "text.disabled" }} />;
    };

    const requestParams = (infos) => {
        if (infos === null) return infos;
        for (const validation of infos) {
            if (!validation.transaction) continue;
            if (validation?.startDate === null) {
                validation.startDate = DateUtil.formatDate(new ZDate(now.getFullYear(), now.getMonth(), 1), "yyyy-MM-dd");
            }
            if (validation?.endDate === null) {
                validation.endDate = DateUtil.formatDate(now, "yyyy-MM-dd");
            }
        }
        return infos;
    };

    /*
     * 검사 실행 공용 루틴.
     *   titles 가 null 이면 전체, 배열이면 그 항목만 돈다(서버가 요청 목록으로 범위를 좁힌다).
     *   detail 은 배너 부제로 쓸 문구 — Sync 후 재검사처럼 "왜 돌았는지" 를 남길 때 넘긴다.
     *
     * 완료는 스낵바가 아니라 화면 위 배너로 알린다 — File Upload & Validation 과 같은 방식.
     * 검사 결과는 "끝났다" 한 마디로 끝나지 않고 오류 건수를 보고 다음 행동을 정해야 하는데,
     * 우측 하단 스낵바는 몇 초 뒤 사라지고 건수도 담지 못한다.
     * (실패는 스낵바를 유지한다 — 그 경로는 결과가 없어 배너에 실을 것이 없다.)
     */
    const runValidation = (titles = null, detail = null) => {
        const allItems = requestParams(validationInfos) ?? [];
        const paramValidationItem = titles ? allItems.filter(item => titles.includes(item.title)) : allItems;

        setValidationRun(true);
        setRunningTitles(titles);
        setValidationResult(null);

        probeJobs();

        /*
         * 배너와 진행 해제는 **갱신된 숫자가 들어온 뒤** 에 한다.
         *   예전에는 요청이 끝나자마자 validationRun 을 내려서, retrieveValidations() 응답이
         *   오기 전 1~2초 동안 "끝났다" 는 화면에 sync 이전 숫자가 그대로 보였다.
         *   배너도 같은 이유로 옛 건수를 실었다.
         */
        let bannerDetail = null;

        dataLoadValidationService.dataValidation(paramValidationItem)
            .then((response) => {
                bannerDetail = detail ?? t(response?.data);
            })
            .catch((error) => {
                const { response } = error;
                const cleaned = response?.data?.replace("[", "").replace("]", "");
                const pairs = cleaned ? cleaned.split(",").map(s => s.trim()) : [];
                const result = {};
                const errorValues = [];
                pairs.forEach(p => {
                    const [key, value] = p.split(":").map(s => s.trim());
                    if (key) {
                        result[t(key ?? "")] = t(value ?? "");
                        errorValues.push(value);
                    }
                });
                const message = `[${Object.entries(result)
                    .map(([key, val]) => `${key}: ${val ?? ""}`)
                    .join(", ")}]`;
                // 
                const isOnlyNoData = errorValues.length > 0 && errorValues.every(val => val === "MSG_NoData");
                const severity = isOnlyNoData ? snackBarMessageColor.dataEmpty : snackBarMessageColor.saveFail;

                setSnackInfo({ open: true, severity: severity, content: message });
                debug(error);
            })
            .finally(() => {
                retrieveValidations().finally(() => {
                    if (bannerDetail !== null) {
                        setValidationResult({ detail: bannerDetail, titles });
                    }
                    setValidationRun(false);
                    setRunningTitles(null);
                });
            });
    };

    const validationExecute = () => runValidation();

    const onRemainingClicked = (title) => {
        const reduxKey = "IMPORT_HISTORY";
        const reduxState = reduxUtil.createObjectState(reduxKey, { searchCode: { jobModule: "Validation", jobTable: t(title) } });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
    };

    const onSyncClicked = (title) => {
        dataLoadValidationService.syncValidation(title)
            .then((response) => {
                const { data } = response;
                if (data?.success) {
                    appConfigService.upsertAppConfigByCd({ configCd: SYNC_CONFIG_CD, saveData: { [title]: new Date().toISOString() } })
                        .then(() => { retrieveLastSyncDates(); })
                        .catch((error) => { debug(error); });

                    /*
                     * Sync 는 데이터를 고칠 뿐 검사 결과를 다시 계산하지 않는다
                     * (BulkImportService.syncValidationData — 고아 행 삭제·활성 플래그 동기화만 한다).
                     * 그래서 예전에는 고친 뒤에도 화면의 오류 건수가 그대로 남아 "고쳤는데 왜 그대로지?" 가 됐다.
                     * 고친 직후 **그 항목만** 다시 검사해 숫자를 맞춘다 — 완료는 위 배너가 알린다.
                     */
                    runValidation([title], `${t(title)} · ${t("MSG_SyncSuccess")}`);
                } else {
                    setSnackInfo({
                        open: true,
                        severity: snackBarMessageColor.saveFail,
                        content: `${t("MSG_SyncFailed")}: ${data?.message || ""}`
                    });
                }
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.message || error.message || t("MSG_ServerError");
                setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: errorMessage });
                debug(error);
            });
    };

    const onAcceptHandler = (dateValue, type, title) => {
        for (const validation of validationInfos) {
            if (validation.title !== title) continue;
            if (isEqual(type, START)) {
                dateValue.setHours(0);
                dateValue.setMinutes(0);
                dateValue.setSeconds(0);
                validation.startDate = DateUtil.formatDate(dateValue, "yyyy-MM-dd");
            } else {
                dateValue.setHours(23);
                dateValue.setMinutes(59);
                dateValue.setSeconds(59);
                validation.endDate = DateUtil.formatDate(dateValue, "yyyy-MM-dd");
            }
        }
    };

    useEffect(() => {
        retrieveValidations();
        retrieveLastSyncDates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box
            id={"data-validation"}
            sx={{ width: "100%", height: "100%", overflow: "auto", display: "flex", flexDirection: "column", gap: 1.5 }}
        >
            {/*
              머리글 — File Upload & Validation 과 같은 규격.
                예전에는 Accordion(expanded 고정)을 카드 껍데기로 쓰고, 마지막 검증일을 ml:auto 로
                오른쪽 끝에 보냈다. 넓은 화면에서 버튼과 날짜가 화면 양끝으로 갈라져 한 묶음으로 안 읽혔다.
                제목 아래 배지로 상태를 모으고, 실행 버튼은 그 아래 왼쪽에 둔다.
            */}
            <AppCard sx={{ flexShrink: 0, px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Box component="span" sx={SECTION_TITLE_SX}>{t("dataValidation")}</Box>
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                        <StatPill
                            accent={totalErrorCount > 0}
                            label={t("errorCnt")}
                            value={totalErrorCount.toLocaleString()}
                        />
                        <StatPill label={t("lastValidationDate")} value={lastValidationLabel} />
                        {(validationRun || serverRunning) && (
                            <StatPill accent label={t("importWorkState")} value={t("running")} />
                        )}
                    </Box>
                </Box>
                {/*
                  오류 요약 — 배지의 "오류건수 58" 만으로는 어디를 봐야 하는지 알 수 없다.
                  카테고리가 20개 넘게 쌓여 있어 문제 항목을 찾으려면 끝까지 훑어야 했다.
                  문제가 있는 항목만 건수 순으로 올려 두고, 누르면 그 카드로 내려간다.
                  오류가 없으면 아예 그리지 않는다 — 조용할 때는 조용해야 눈에 띌 때 잡힌다.
                */}
                {failingInfos.length > 0 && (
                    <Box
                        sx={{
                            display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap",
                            p: 1.25, borderRadius: 1.5,
                            border: 1, borderColor: "warning.light", bgcolor: "#fffbf5",
                        }}
                    >
                        <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: "warning.dark", flexShrink: 0 }} />
                        {failingInfos.map(item => (
                            <Box
                                key={item.title}
                                onClick={() => scrollToCard(item.title)}
                                sx={{ cursor: "pointer", "&:hover": { filter: "brightness(0.96)" } }}
                            >
                                <StatPill accent label={t(item.title)} value={item.count.toLocaleString()} />
                            </Box>
                        ))}
                    </Box>
                )}
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<FactCheckOutlinedIcon />}
                    sx={{ alignSelf: "flex-start", textTransform: "none" }}
                    disabled={validationRun || serverRunning}
                    onClick={() => handleDialogOpen(DIALOG_TITLE.DATA_VALIDATION, t("all"), validationExecute)}
                >
                    {t("conductDataValidation")}
                    {validationRun && <CircularProgress sx={{ ml: 1 }} size={16} />}
                </Button>
            </AppCard>

            {/* 방금 끝난 검사 결과 — 오류가 있으면 경고 톤으로, 없으면 성공 톤으로 결말을 분명히 말한다 */}
            {validationResult && (() => {
              /* 방금 돈 범위의 건수만 본다 — 전체 합계를 쓰면 고친 항목의 결과가 묻힌다 */
              const bannerErrorCount = errorCountFor(validationResult.titles ?? null);
              return (
                <Box
                    sx={{
                        flexShrink: 0, borderRadius: 1.5, p: 1.5, border: 1,
                        borderColor: bannerErrorCount > 0 ? "warning.light" : "success.light",
                        bgcolor: bannerErrorCount > 0 ? "#fffbf5" : "#f6faf6",
                        display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
                    }}
                >
                    {bannerErrorCount > 0
                        ? <WarningAmberOutlinedIcon sx={{ color: "warning.dark" }} />
                        : <CheckCircleRoundedIcon sx={{ color: "success.main" }} />}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                        <Box component="span" sx={{ ...CARD_HEADER_SX, color: bannerErrorCount > 0 ? "warning.dark" : "success.dark" }}>
                            {t("MSG_ValidationComplete")}
                        </Box>
                        {validationResult.detail && (
                            <Box component="span" sx={{ fontSize: FS.SUB, color: "text.secondary" }}>
                                {validationResult.detail}
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                        <StatPill
                            accent={bannerErrorCount > 0}
                            label={t("errorCnt")}
                            value={bannerErrorCount.toLocaleString()}
                        />
                        <IconButton size="small" onClick={() => setValidationResult(null)}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                </Box>
              );
            })()}

            {/* 검증 항목 카드들 — 부모(root)와 같은 간격으로 쌓는다 */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pb: 1 }}>
                {validationInfos?.map(validationInfo => {
                    const detail = `${validationInfo.title}Detail`;
                    const hasTitleList = validationInfo.titleList && validationInfo.titleList.length > 0;
                    const dataCount = hasTitleList
                        ? validationInfo.titleList.reduce((sum, titleItem) => sum + (titleItem.dataCount || 0), 0)
                        : validationInfo?.dataCount;

                    const cardRunning = isTitleRunning(validationInfo.title);

                    return (
                        /*
                          돌고 있는 항목은 카드를 흐리게 둔다 — 지금 보이는 숫자가 아직 옛 결과라는 뜻이다.
                          (버튼은 이미 비활성이므로 pointerEvents 로 막지 않는다 — 스크롤·선택은 되어야 한다.)
                        */
                        <AppCard
                            key={validationInfo.title}
                            id={cardDomId(validationInfo.title)}
                            sx={{
                                flexShrink: 0,
                                opacity: cardRunning ? 0.55 : 1,
                                transition: "opacity .2s ease",
                            }}
                        >
                            {/*
                              머리글 — 상태 아이콘 + 이름 + 오류 배지 + 동작.
                                오류 건수를 배지로 올려, 카드를 펼쳐 읽지 않아도 어디가 문제인지 보이게 한다.
                            */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                px: 2, py: 1.25, borderBottom: 1, borderColor: "divider", flexWrap: "wrap",
                            }}>
                                <ValidationStateIcon title={validationInfo.title} dataCount={dataCount} lastValidationDate={validationInfo.lastValidationDate} noDataFlg={validationInfo.noDataFlg} />
                                <Box component="span" sx={CARD_HEADER_SX}>{t(validationInfo.title)}</Box>
                                <StatPill accent={dataCount > 0} label={t("errorCnt")} value={Number(dataCount || 0).toLocaleString()} />
                                <Box sx={{ flexGrow: 1 }} />
                                {/*
                                  항목별 검증 — 서버가 요청에 실린 항목만 돌게 되면서(BulkImportController)
                                  카테고리 하나만 다시 볼 수 있다. 전체 검증은 머리글 버튼에 그대로 남는다.
                                  진행 중일 때는 전부 비활성 — 서버가 중복 실행을 거부하기 때문이다.
                                */}
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<FactCheckOutlinedIcon />}
                                    sx={{ textTransform: "none" }}
                                    disabled={validationRun || serverRunning}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDialogOpen(
                                            DIALOG_TITLE.DATA_VALIDATION,
                                            validationInfo.title,
                                            () => runValidation([validationInfo.title], t(validationInfo.title))
                                        );
                                    }}
                                >
                                    {t("validation")}
                                </Button>
                                {syncTargets.includes(validationInfo.title) && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={cardRunning ? <CircularProgress size={14} /> : <SyncIcon />}
                                        sx={{ textTransform: "none" }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDialogOpen(DIALOG_TITLE.SYNC, validationInfo, () => onSyncClicked(validationInfo.title));
                                        }}
                                        disabled={
                                            validationRun || serverRunning ||
                                            !validationInfo.titleList ||
                                            validationInfo.titleList.length === 0 ||
                                            validationInfo.titleList.filter(item => item.syncable !== false && (item.dataCount || 0) > 0).length === 0
                                        }
                                    >
                                        {t("sync")}
                                    </Button>
                                )}
                                {/* 이력 화면이 권한 밖이면 "상세" 버튼을 내보내지 않는다 */}
                                {isPermitted("IMPORT_HISTORY") && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        endIcon={<ChevronRightIcon />}
                                        sx={{ textTransform: "none" }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemainingClicked(validationInfo.title);
                                        }}
                                    >
                                        {t("detail")}
                                    </Button>
                                )}
                            </Box>
                            {/*
                              본문 — ul/li + Typography 중첩을 걷어냈다.
                                전역 SCSS(.content-main .MuiTypography-root)가 크기를 덮어써 항목마다 글자가 튀었고,
                                li 안에 Typography 를 또 넣는 구조라 여백이 규격 밖에서 만들어지고 있었다.
                            */}
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                                        {!hasTitleList && (
                                            <Box component="span" sx={{ fontSize: FS.SUB, color: "text.secondary" }}>
                                                {`${t("check")} : ${t(detail)}`}
                                            </Box>
                                        )}
                                        {hasTitleList && (
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                                <Box component="span" sx={{ fontSize: FS.CAPTION, fontWeight: 700, letterSpacing: "0.04em", color: "text.secondary" }}>
                                                    {t("checkList")}
                                                </Box>
                                                {validationInfo.titleList.map((titleItem, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            display: 'flex', alignItems: 'center', gap: 1,
                                                            px: 1.25, py: 0.75, borderRadius: 1.5,
                                                            bgcolor: titleItem.dataCount > 0 ? "#fdf5f4" : "grey.50",
                                                        }}
                                                    >
                                                        <ValidationStateIcon
                                                            title={validationInfo.title}
                                                            dataCount={titleItem.dataCount}
                                                            lastValidationDate={titleItem.lastValidationDate}
                                                            noDataFlg={titleItem.noDataFlg}
                                                        />
                                                        <Box component="span" sx={{ flex: 1, fontSize: FS.SUB, color: "text.primary" }}>
                                                            {t(titleItem.title, titleItem.params ? Object.fromEntries(
                                                                Object.entries(titleItem.params).map(([key, value]) => [key, t(value)])
                                                            ) : {})}
                                                        </Box>
                                                        <StatPill
                                                            accent={titleItem.dataCount > 0}
                                                            label={t("errorCnt")}
                                                            value={Number(titleItem.dataCount || 0).toLocaleString()}
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                        {/* 기간 조건 — 다른 화면과 같이 이름표를 단 세로 묶음으로 */}
                                        {validationInfo?.transaction && (
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                                <Box component="span" sx={{ fontSize: FS.CAPTION, fontWeight: 700, letterSpacing: "0.04em", color: "text.secondary" }}>
                                                    {t("periodSearchCondition")}
                                                </Box>
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                        <DatePicker
                                                            label={t("periodStart")}
                                                            views={[YEAR, MONTH, DAY]}
                                                            format={"yyyy-MM-dd"}
                                                            openTo={DAY}
                                                            value={validationInfo?.startDate ?? DateUtil.formatDate(new ZDate(now.getFullYear(), now.getMonth(), 1), "yyyy-MM-dd")}
                                                            onAccept={(e) => onAcceptHandler(e, START, validationInfo.title)}
                                                            slotProps={{ textField: { InputProps: { color: 'primary', size: "small", sx: { width: 168 } } } }}
                                                        />
                                                        <DatePicker
                                                            label={t("periodEnd")}
                                                            views={[YEAR, MONTH, DAY]}
                                                            format={"yyyy-MM-dd"}
                                                            openTo={DAY}
                                                            value={validationInfo?.endDate ?? DateUtil.formatDate(new ZDate(now), "yyyy-MM-dd")}
                                                            onAccept={(e) => onAcceptHandler(e, END, validationInfo.title)}
                                                            slotProps={{ textField: { InputProps: { color: 'primary', size: "small", sx: { width: 168 } } } }}
                                                        />
                                                    </LocalizationProvider>
                                                </Box>
                                            </Box>
                                        )}
                                        {syncTargets.includes(validationInfo.title) && (
                                            <StatPill
                                                sx={{ alignSelf: "flex-start" }}
                                                label={t("lastSyncDate")}
                                                value={lastSyncDates?.[validationInfo.title]
                                                    ? DateUtil.formatDate(lastSyncDates?.[validationInfo.title], "yyyy-MM-dd")
                                                    : "-"}
                                            />
                                        )}
                                </Box>
                            </Box>
                        </AppCard>
                    );
                })}
            </Box>

            <Snackbar
                open={snackInfo.open}
                onClose={() => setSnackInfo({ ...snackInfo, open: false })}
                severity={snackInfo.severity}
                content={snackInfo.content}
                duration={15000}
            />
            <Dialog
                open={dialogInfo.open}
                onClose={onDialogClose}
                title={dialogInfo.title}
                content={dialogInfo.content}
                onHandler={handleDialog}
                el={dialogInfo.el}
                maxWidth={dialogInfo.maxWidth}
            />
        </Box>
    );
};

export default withTranslation()(DataValidation);